import { useEffect, useState } from "react";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import ShareTheQR from "../components/ShareTheQR";
import { getFrontPorchPage, subscribe } from "../lib/api";
import { safeHtml } from "../lib/safeHtml";

const DEFAULT_PAGE = {
  seo_title: "Front Porch Swing",
  seo_description:
    "Settle into the swing. We've been saving you a seat. Subscribe for gentle pasture stories, new books, and the occasional Saltie Wink\u2122.",
  hero_title: "Front Porch Swing",
  hero_subtitle: "Settle in. We\u2019ve been saving you a seat.",
  photo_caption_name: "Stefanie & Mike",
  photo_caption_subtitle: "where the story continues",
  intro_body_html:
    "<p>Because sometimes the most important chapters begin before you ever turn the page. Coffee, sweet tea, or lemonade... grab your favorite and join us on the porch swing. We\u2019re just glad you\u2019re here.</p>",
  peek_a_moo_heading: "psst\u2026 a little secret",
  peek_a_moo_body_html:
    "<p>Somewhere in this storybook, our friend <em>Peek-a-Moo\u2122</em> is hiding. Churro moves from page to page \u2014 tucked into pastures, porches, and barns. Keep your eyes open as you turn each page, and if you spot Churro, give a little tap.</p>",
  newsletter_heading: "Settle Into the Swing.",
  newsletter_script_line_1: "stay a while",
  newsletter_script_line_2: "We've been saving you a seat.",
  newsletter_body:
    "Gentle pasture stories, new books, and the occasional Saltie Wink\u2122 \u2014 delivered no more often than feels peaceful.",
  substack_footer_note: "Powered by Substack. One-click unsubscribe any time, no hard feelings.",
  next_button_label: "Discover the Salt Block",
};

export default function FrontPorch() {
  const [page, setPage] = useState(DEFAULT_PAGE);
  // Native newsletter signup — replaces the previous Substack iframe.
  // Uses the existing POST /api/subscribe endpoint (Resend welcome email
  // is triggered server-side; duplicates are caught server-side too).
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | already | error
  const [message, setMessage] = useState("");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (status === "submitting") return; // prevent rapid double-submit
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const res = await subscribe(value);
      if (res.already_subscribed) {
        setStatus("already");
        setMessage(res.message || "You\u2019re already part of the herd, friend.");
      } else {
        setStatus("success");
        setMessage(res.message || "Welcome to the pasture! We\u2019re so glad you\u2019re here.");
        setEmail("");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again in a moment.");
    }
  };

  useEffect(() => {
    getFrontPorchPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);
  return (
    <div className="parchment min-h-screen" data-testid="front-porch-page">
      <SEO
        title={page.seo_title}
        description={page.seo_description}
        path="/front-porch"
        image="https://thesaltymoo.com/assets/heroes/front-porch.webp"
      />

      <ChapterHero
        path="/front-porch"
        image="/assets/heroes/front-porch.webp"
        title={page.hero_title}
        subtitle={page.hero_subtitle}
        position="top-left"
      />

      <div className="max-w-4xl mx-auto px-6 -mt-8 relative z-10 pb-24">
        <div className="grid md:grid-cols-2 gap-10 items-center mt-8">
          <figure className="relative">
            {/* Layered storybook frame: outer cream mat + deckle drop + inner wood frame */}
            <div
              className="relative bg-[#F5EDDE] p-3 sm:p-4"
              style={{
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 22px 30px -18px rgba(0,0,0,0.45), 0 2px 0 rgba(75,58,47,0.18)",
                borderRadius: "2px",
                transform: "rotate(-0.5deg)",
              }}
            >
              <span
                aria-hidden
                className="absolute -top-3 left-6 w-16 h-5 rotate-[-8deg] opacity-70"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,247,210,0.85), rgba(232,210,140,0.55))",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                }}
              />
              <span
                aria-hidden
                className="absolute -top-3 right-6 w-16 h-5 rotate-[7deg] opacity-70"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,247,210,0.85), rgba(232,210,140,0.55))",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                }}
              />

              <div
                data-testid="front-porch-photo"
                className="relative w-full overflow-hidden"
                style={{
                  aspectRatio: "1448 / 1086",
                  border: "6px solid #7E9268",
                  boxShadow:
                    "inset 0 0 0 1px rgba(255,255,255,0.12), inset 0 0 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(75,58,47,0.18)",
                  borderRadius: "1px",
                }}
              >
                <img
                  src="/assets/porch-swing.webp"
                  alt="Stefanie and Mike on the porch swing at sunset"
                  className="absolute inset-0 w-full h-full select-none"
                  draggable="false"
                />
              </div>
            </div>

            <figcaption
              data-testid="front-porch-caption"
              className="mt-6 text-center"
              style={{ transform: "rotate(-0.5deg)" }}
            >
              <p className="font-script text-2xl md:text-3xl text-[#4B3A2F] leading-tight">
                {page.photo_caption_name}
              </p>
              <p className="mt-1 text-xs md:text-sm uppercase tracking-[0.35em] text-[#6D5A50]">
                {page.photo_caption_subtitle}
              </p>
              <div className="gold-rule mx-auto mt-4 w-24" />
            </figcaption>
          </figure>

          <div
            className="space-y-5 font-body text-[#2C2724]/90 text-lg leading-relaxed font-display italic text-2xl text-[#4B3A2F] prose-winks"
            dangerouslySetInnerHTML={safeHtml(page.intro_body_html)}
          />
        </div>

        {/* Peek-a-Moo™ hide-and-seek explainer */}
        <div
          data-testid="peek-a-moo-explainer"
          className="mt-16 max-w-3xl mx-auto parchment-deep border-2 border-[#A87149]/50 rounded-md page-shadow px-10 py-9 flex flex-col sm:flex-row items-center gap-7 text-center sm:text-left"
        >
          <img
            src="/assets/peek-a-moo.webp"
            alt=""
            aria-hidden="true"
            className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 object-contain select-none peek-wiggle"
            draggable="false"
            style={{ transformOrigin: "50% 70%" }}
          />
          <div>
            <p className="font-script text-3xl md:text-4xl text-[#4B3A2F] leading-tight mb-2">
              {page.peek_a_moo_heading}
            </p>
            <div
              className="font-body text-lg md:text-xl text-[#2C2724]/90 leading-relaxed prose-winks"
              dangerouslySetInnerHTML={safeHtml(page.peek_a_moo_body_html)}
            />
          </div>
        </div>

        {/* Newsletter — native "Join the Herd" form (Resend delivers welcome) */}
        <div className="mt-20 parchment-deep border border-[#E7DFD5] rounded-md page-shadow p-6 sm:p-10 max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl text-[#4B3A2F] mb-2">
            {page.newsletter_heading}
          </h2>
          <p className="font-script text-3xl text-[#4B3A2F] mb-2">
            {page.newsletter_script_line_1}
          </p>
          <p className="font-script text-2xl md:text-3xl text-[#4B3A2F] mb-5">
            {page.newsletter_script_line_2}
          </p>
          <p className="font-body text-[#2C2724]/80 mb-7 max-w-md mx-auto text-sm sm:text-base leading-relaxed">
            {page.newsletter_body}
          </p>

          <form
            onSubmit={onSubscribe}
            data-testid="newsletter-form"
            className="w-full max-w-md mx-auto"
            noValidate
            style={{ boxSizing: "border-box" }}
          >
            <label htmlFor="newsletter-email" className="sr-only">
              Email address
            </label>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch">
              <input
                id="newsletter-email"
                data-testid="newsletter-email-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status !== "idle" && status !== "submitting") {
                    setStatus("idle");
                    setMessage("");
                  }
                }}
                disabled={status === "submitting"}
                aria-invalid={status === "error"}
                aria-describedby="newsletter-status"
                className="flex-1 w-full min-h-[44px] px-4 py-3 rounded-md bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-base text-[#2C2724] placeholder:text-[#8D7A6E] disabled:opacity-60"
                style={{ boxSizing: "border-box" }}
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={status === "submitting" || !email.trim()}
                aria-busy={status === "submitting"}
                className="btn-pasture min-h-[44px] w-full sm:w-auto whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "submitting" ? "Joining\u2026" : "Join the Herd"}
              </button>
            </div>

            {/* Live status region — screen readers announce changes */}
            <p
              id="newsletter-status"
              data-testid="newsletter-status"
              role="status"
              aria-live="polite"
              className={
                "mt-4 text-sm sm:text-base font-body italic leading-relaxed " +
                (status === "error"
                  ? "text-[#D62839]"
                  : status === "success" || status === "already"
                  ? "text-[#4B3A2F]"
                  : "text-transparent")
              }
            >
              {message || "\u00a0"}
            </p>
          </form>
        </div>

        <ShareTheQR />
      </div>

      <NextPage to="/salt-block" label={page.next_button_label} />
    </div>
  );
}
